/*
 * A dialog.
 *
 * Escape closes it, the scrim closes it, and focus moves into it when it
 * opens — a modal that has to be dismissed with the mouse is a modal that
 * cannot be dismissed by somebody working from the keyboard, which on this
 * screen is most of the time.
 *
 * The body scroll is locked while one is open, because a dialog over a page
 * that scrolls underneath it loses the reader's place.
 */

import { useEffect, useRef, type ReactNode } from "react";

import { IconClose } from "./Icons";

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

export function Modal({ title, onClose, children, footer, wide }: Props) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // The first control in the dialog, or the dialog itself if it has none.
    const focusable = panel.current?.querySelector<HTMLElement>(
      "input, select, textarea, button:not(.icon-btn)",
    );
    (focusable ?? panel.current)?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="modal-scrim"
      onMouseDown={(e) => {
        // Only a press that both started and ended on the scrim. Otherwise a
        // drag that begins inside the dialog and releases outside it closes
        // the thing the person was in the middle of using.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={wide ? "modal wide" : "modal"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <IconClose size={17} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
